"use client"

import * as React from "react"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { IconShare, IconLoader2, IconBraces, IconCoin } from "@tabler/icons-react"
import { LuGroup } from "react-icons/lu"
import { toast } from "sonner"

import { useActivityLogDetail } from "@/data/hooks/api/useActivityLogs"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { ProviderIcon } from "@/components/provider-pill"
import { Chip } from "@/components/Chip"
import { cn, formatTimestamp, formatCurrency } from "@/lib/utils"

interface TransactionDetailsSidebarProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  transactionId: string | null
}

// Compact table component for pricing breakdown
function PricingTable({ data }: { data: [string, string][] }) {
  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-[32px] text-center">
        <div className="flex items-center justify-center size-10 rounded-full bg-[#F7F8F8] mb-2">
          <IconCoin className="size-4 text-[#8A8F98]" />
        </div>
        <span className="text-[12px] text-[#8A8F98]">No pricing breakdown available</span>
      </div>
    )
  }

  return (
    <div className="rounded-[6px] border border-[#EDEEF3] overflow-hidden bg-white">
      <table className="w-full text-[12px]">
        <thead className="bg-[#F7F8F8] border-b border-[#EDEEF3]">
          <tr>
            <th className="w-2/5 h-[32px] px-[12px] text-left text-[11px] font-medium text-[#8A8F98] uppercase tracking-wide">
              Name
            </th>
            <th className="w-3/5 h-[32px] px-[12px] text-left text-[11px] font-medium text-[#8A8F98] uppercase tracking-wide">
              Value
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#EDEEF3]">
          {data.map(([key, value], index) => {
            const isTotal = key.toLowerCase() === "total"
            return (
              <tr
                key={`${key}-${index}`}
                className={cn(
                  "h-[32px] transition-colors",
                  isTotal && "bg-[#F7F8F8]/50 border-t-2 border-[#EDEEF3]"
                )}
              >
                <td className={cn(
                  "px-[12px] text-[#8A8F98]",
                  isTotal && "font-semibold text-[#08090A]"
                )}>
                  {key}
                </td>
                <td className={cn(
                  "px-[12px] font-mono text-[#08090A]",
                  isTotal && "font-semibold"
                )}>
                  {value}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// Compact table component for metadata
function MetadataTable({ data }: { data: [string, string][] }) {
  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-[32px] text-center">
        <div className="flex items-center justify-center size-10 rounded-full bg-[#F7F8F8] mb-2">
          <IconBraces className="size-4 text-[#8A8F98]" />
        </div>
        <span className="text-[12px] text-[#8A8F98]">No metadata available</span>
      </div>
    )
  }

  return (
    <div className="rounded-[6px] border border-[#EDEEF3] overflow-hidden bg-white">
      <table className="w-full text-[12px]">
        <thead className="bg-[#F7F8F8] border-b border-[#EDEEF3]">
          <tr>
            <th className="w-2/5 h-[32px] px-[12px] text-left text-[11px] font-medium text-[#8A8F98] uppercase tracking-wide">
              Key
            </th>
            <th className="w-3/5 h-[32px] px-[12px] text-left text-[11px] font-medium text-[#8A8F98] uppercase tracking-wide">
              Value
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#EDEEF3]">
          {data.map(([key, value], index) => (
            <tr key={`${key}-${index}`} className="h-[32px] transition-colors hover:bg-[#F7F8F8]">
              <td className="px-[12px] text-[#8A8F98] font-mono text-[11px]">{key}</td>
              <td className="px-[12px] font-mono text-[#08090A] truncate max-w-[300px]" title={value}>
                {value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// Summary table showing transaction overview (same info as parent row)
function SummaryTable({
  timestamp,
  provider,
  model,
  modality,
  cost,
  usage
}: {
  timestamp: string
  provider: string
  model: string
  modality: string
  cost: number
  usage: string
}) {
  return (
    <div className="rounded-[6px] border border-[#EDEEF3] overflow-hidden bg-white">
      <table className="w-full text-[12px]">
        <thead className="bg-[#F7F8F8] border-b border-[#EDEEF3]">
          <tr>
            <th className="w-2/5 h-[32px] px-[12px] text-left text-[11px] font-medium text-[#8A8F98] uppercase tracking-wide">
              Field
            </th>
            <th className="w-3/5 h-[32px] px-[12px] text-left text-[11px] font-medium text-[#8A8F98] uppercase tracking-wide">
              Value
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#EDEEF3]">
          <tr className="h-[32px]">
            <td className="px-[12px] text-[#8A8F98]">Timestamp</td>
            <td className="px-[12px] font-mono text-[#08090A]">{formatTimestamp(timestamp)}</td>
          </tr>
          <tr className="h-[32px]">
            <td className="px-[12px] text-[#8A8F98]">Provider</td>
            <td className="px-[12px]">
              <div className="flex items-center gap-2">
                <ProviderIcon provider={provider} className="w-4 h-4" />
                <span className="text-[#08090A]">{provider}</span>
              </div>
            </td>
          </tr>
          <tr className="h-[32px]">
            <td className="px-[12px] text-[#8A8F98]">Model</td>
            <td className="px-[12px]">
              <code className="rounded bg-[#F7F8F8] border border-[#EDEEF3] px-[6px] py-[2px] text-[11px] font-mono text-[#08090A]">
                {model}
              </code>
            </td>
          </tr>
          <tr className="h-[32px]">
            <td className="px-[12px] text-[#8A8F98]">Modality</td>
            <td className="px-[12px]">
              <Chip
                label={modality.charAt(0).toUpperCase() + modality.slice(1).toLowerCase()}
                icon={<LuGroup className="size-3 text-[#654EDA]" />}
              />
            </td>
          </tr>
          <tr className="h-[32px]">
            <td className="px-[12px] text-[#8A8F98]">Cost</td>
            <td className="px-[12px] font-mono text-[#08090A]">{formatCurrency(cost, 6, 6)}</td>
          </tr>
          <tr className="h-[32px]">
            <td className="px-[12px] text-[#8A8F98]">Usage</td>
            <td className="px-[12px] font-mono text-[#08090A]">{usage}</td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

export function TransactionDetailsSidebar({
  open,
  onOpenChange,
  transactionId,
}: TransactionDetailsSidebarProps) {
  const { data: transaction, isLoading } = useActivityLogDetail(transactionId)

  const handleCopyLink = React.useCallback(() => {
    if (!transactionId) return

    const url = new URL(window.location.href)
    url.searchParams.set("transaction_id", transactionId)
    navigator.clipboard.writeText(url.toString())
    toast.success("Link copied to clipboard")
  }, [transactionId])

  const handleCopyId = React.useCallback(() => {
    if (!transactionId) return
    navigator.clipboard.writeText(transactionId)
    toast.success("Transaction ID copied")
  }, [transactionId])

  return (
    <SheetPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <SheetPrimitive.Portal>
        <SheetPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-black/40",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          )}
        />
        <SheetPrimitive.Content
          className={cn(
            "fixed z-50 flex flex-col bg-white shadow-lg",
            "inset-y-0 right-0 h-full w-[620px] border-l border-[#EDEEF3]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
            "data-[state=closed]:duration-200 data-[state=open]:duration-300"
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-[16px] py-[12px] border-b border-[#EDEEF3]">
            <div className="flex flex-col">
              <SheetPrimitive.Title className="text-[16px] font-semibold text-[#08090A]">
                Transaction Details
              </SheetPrimitive.Title>
              {transactionId && transaction && (
                <div className="flex items-center gap-2 mt-1 text-[12px]">
                  <span className="text-[#8A8F98]">Transaction ID:</span>
                  <button
                    onClick={handleCopyId}
                    className="text-[#654EDA] hover:text-[#4F3BB3] font-mono cursor-pointer hover:underline"
                    title="Click to copy"
                  >
                    {transactionId}
                  </button>
                  <span className="text-[#EDEEF3]">|</span>
                  <span className="text-[#8A8F98]">{formatTimestamp(transaction.timestamp)}</span>
                </div>
              )}
            </div>
            <SheetPrimitive.Description className="sr-only">
              View transaction pricing breakdown and metadata
            </SheetPrimitive.Description>
            <button
              onClick={handleCopyLink}
              className="flex items-center justify-center size-[28px] rounded-[6px] text-[#8A8F98] hover:text-[#08090A] hover:bg-[#F7F8F8] transition-colors cursor-pointer"
              title="Copy link"
            >
              <IconShare className="size-4" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-[48px] text-center">
                <IconLoader2 className="size-5 text-[#654EDA] animate-spin mb-[10px]" />
                <p className="text-[12px] text-[#8A8F98]">Loading transaction details...</p>
              </div>
            ) : transaction ? (
              <div className="flex flex-col">
                {/* Summary Section */}
                <div className="px-[16px] pt-[16px]">
                  <h3 className="text-[12px] font-medium text-[#8A8F98] uppercase tracking-wide mb-[8px]">
                    Overview
                  </h3>
                  <SummaryTable
                    timestamp={transaction.timestamp}
                    provider={transaction.provider}
                    model={transaction.model}
                    modality={transaction.modality}
                    cost={transaction.cost}
                    usage={transaction.usage}
                  />
                </div>

                {/* Tabs for Pricing & Metadata */}
                <Tabs defaultValue="pricing" className="flex flex-col flex-1 mt-[16px]">
                  <div className="px-[16px]">
                    <TabsList className="bg-[#F7F8F8] border border-[#EDEEF3] w-full h-[32px] p-[2px] rounded-[6px]">
                      <TabsTrigger
                        value="pricing"
                        className="flex-1 text-[12px] h-[26px] rounded-[4px] data-[state=active]:bg-white data-[state=active]:text-[#08090A] data-[state=active]:shadow-sm text-[#8A8F98]"
                      >
                        Pricing Breakdown
                      </TabsTrigger>
                      <TabsTrigger
                        value="metadata"
                        className="flex-1 text-[12px] h-[26px] rounded-[4px] data-[state=active]:bg-white data-[state=active]:text-[#08090A] data-[state=active]:shadow-sm text-[#8A8F98]"
                      >
                        Metadata
                      </TabsTrigger>
                    </TabsList>
                  </div>

                  <TabsContent value="pricing" className="flex-1 p-[16px] pt-[12px]">
                    <PricingTable data={transaction.pricing_breakdown} />
                  </TabsContent>

                  <TabsContent value="metadata" className="flex-1 p-[16px] pt-[12px]">
                    <MetadataTable data={transaction.metadata} />
                  </TabsContent>
                </Tabs>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-[48px] text-center">
                <p className="text-[12px] text-[#8A8F98]">
                  Transaction not found
                </p>
              </div>
            )}
          </div>
        </SheetPrimitive.Content>
      </SheetPrimitive.Portal>
    </SheetPrimitive.Root>
  )
}
